import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import {
  DepositLimit,
  PasswordConfirm,
  PersonalDetails,
  SecuritySettings,
} from '@mobi/account/Areas/SignUp/Components'
import {
  Passport,
  IdentityDocumentSelector,
  DriversLicence,
  Medicare,
  PhoneNumber,
  type MfaVerificationPage,
} from '@mobi/account/Areas/SignUp/Components/Verification'
import { FailedVerification } from './Message/FailedVerification'
import { LogonRequestData, LogonResult } from '@mobi/api-types'
import { routes, SIGNUP_ROOT_ROUTE, subRoutes } from '../Utils/routes'
import { useRiskAssessment } from '../Hooks/useRiskAssessment'
import { SignUpCompletePage } from './SignUpCompletePage'
import type { Campaign } from '../typings/types'
import { getSignUpState, setIsNewSignupFlowEnabled, setSignupNewSession } from '../Store'
import { useScrollRestoration } from '@mobi/account/Hooks/useScrollRestoration'
import { AdditionalInformation } from './AdditionalInformation'
import { VerificationAccountInformationPage } from '../../VerificationAccount/'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'

type SignUpPageProps = {
  isLoggedIn: boolean | null
  attemptLogin: (data: LogonRequestData) => Promise<
    LogonResult & {
      errorMessage?: string
    }
  >
  showLogin: (options?: { onLoginSuccessRedirectUrl?: string }) => void
  campaign: Campaign | undefined
  openDeposit: () => void
  onMount?: () => void
  onDispose?: () => void
  MfaVerificationPage: React.MemoExoticComponent<MfaVerificationPage>
}

export const SignUpPage = ({
  isLoggedIn,
  attemptLogin,
  showLogin,
  campaign,
  openDeposit,
  onMount,
  onDispose,
  MfaVerificationPage,
}: SignUpPageProps) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [newPassword, setNewPassword] = React.useState('')
  const state = useSelector(getSignUpState)
  const isAmlRiskAssessed = useHostContextFeature(FeatureFlags.AML_SIGNUP_RISK_ASSESSMENT.key)
  // prefetch risk assessment data
  useRiskAssessment({ enabled: isAmlRiskAssessed })
  dispatch(setIsNewSignupFlowEnabled(isAmlRiskAssessed))

  useScrollRestoration()

  useEffect(() => {
    onMount?.()
    return () => {
      dispatch(setSignupNewSession())
      onDispose?.()
    }
  }, [dispatch])

  const applicationRequiredRoutes: string[] = [
    routes.additionalInformation,
    routes.verificationOptions,
    routes.driversLicence,
    routes.passport,
    routes.medicare,
    routes.password,
  ]
  // users must have an application reference number to access any application routes
  if (
    applicationRequiredRoutes.includes(location.pathname) &&
    !state.signupApplication.applicationReferenceNumber
  ) {
    return <Navigate to={routes.contactDetails} />
  }
  const signupVerifiedIdRoutes: string[] = [
    routes.phoneNumberVerification,
    routes.security,
    routes.depositLimit,
    routes.signUpComplete,
  ]
  // users must have a verified identity to access these routes
  if (signupVerifiedIdRoutes.includes(location.pathname) && !state.signupVerification.isVerified) {
    return <Navigate to={routes.home} />
  }

  // logged in users should not be able to access any signup application routes or verification account information page
  if (
    (!signupVerifiedIdRoutes.includes(location.pathname) ||
      location.pathname === routes.verificationAccountInformation) &&
    isLoggedIn
  ) {
    return <Navigate to={routes.home} />
  }

  return (
    <Routes>
      <Route path='/' element={<PersonalDetails />} />
      <Route path={subRoutes.verificationOptions} element={<IdentityDocumentSelector />} />
      <Route path={subRoutes.driversLicence} element={<DriversLicence />} />
      <Route path={subRoutes.passport} element={<Passport />} />
      {!state.isNewSignupFlowEnabled && <Route path={subRoutes.medicare} element={<Medicare />} />}

      <Route path={subRoutes.additionalInformation} element={<AdditionalInformation />} />

      <Route
        path={subRoutes.password}
        element={
          <PasswordConfirm
            onCompleted={password => {
              setNewPassword(password)
            }}
            attemptLogin={attemptLogin}
          />
        }
      />
      <Route
        path={subRoutes.phoneNumberVerification}
        element={<PhoneNumber MfaVerificationPage={MfaVerificationPage} />}
      />
      <Route path={subRoutes.security} element={<SecuritySettings password={newPassword} />} />
      <Route
        path={subRoutes.signUpUnsuccessful}
        element={<FailedVerification showLogin={showLogin} />}
      />
      <Route
        path={subRoutes.signUpComplete}
        element={<SignUpCompletePage campaign={campaign} openDeposit={openDeposit} />}
      />
      <Route path={subRoutes.depositLimit} element={<DepositLimit />} />
      <Route
        path={subRoutes.verificationAccountInformation}
        element={<VerificationAccountInformationPage />}
      />
      <Route path='*' element={<Navigate to={`/${SIGNUP_ROOT_ROUTE}`} />} />
    </Routes>
  )
}

SignUpPage.displayName = 'SignUpPage'

export default SignUpPage
